<script setup lang="ts">
import { useVideoStream } from '@/composables/video/use-video-stream';
import { Spinner } from '@/components/ui/spinner';

type StoryPlayerSegment = {
  id: string;
  src: string;
  hls?: string | null;
  poster?: string | null;
  duration?: number;
  question?: string | null;
};

const props = withDefaults(defineProps<{
  videos: StoryPlayerSegment[];
  active: boolean;
  preload?: boolean;
  /** Rendered in a sheet without the bottom navigation: progress sits lower. */
  sheet?: boolean;
  /** Covered by the details: hold the video, then resume it unless the viewer paused it. */
  suspended?: boolean;
}>(), {
  preload: false,
  sheet: false,
  suspended: false,
});

const emit = defineEmits<{
  finished: [];
  'segment-change': [index: number];
}>();

const videoElement = useTemplateRef<HTMLVideoElement>('videoElement');

const segmentIndex = ref(0);
const currentTime = ref(0);
const duration = ref(0);
const isPaused = ref(true);
const isMuted = ref(true);
const isAutoplayBlocked = ref(false);
const isCompleted = ref(false);
/** The viewer paused the video: lifting `suspended` leaves it paused. */
const isPausedByViewer = ref(false);

const currentVideo = computed<StoryPlayerSegment | null>(() => {
  return props.videos[segmentIndex.value] ?? null;
});

const shouldMountVideo = computed(() => {
  return props.active || props.preload;
});

const preloadMode = computed<'auto' | 'metadata'>(() => {
  return props.active ? 'auto' : 'metadata';
});

const stream = useVideoStream(videoElement, currentVideo, () => props.active);
const isBuffering = stream.isBuffering;

const progress = computed(() => {
  if (duration.value <= 0) {
    return 0;
  }

  return Math.min(currentTime.value / duration.value, 1) * 100;
});

const remainingLabel = computed(() => {
  const seconds = Math.max(0, Math.ceil(duration.value - currentTime.value));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;

  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
});

function getSegmentProgress(index: number): number {
  if (index < segmentIndex.value) {
    return 100;
  }

  if (index > segmentIndex.value) {
    return 0;
  }

  return progress.value;
}

async function playCurrentVideo(): Promise<void> {
  if (!props.active || props.suspended || currentVideo.value === null || videoElement.value === null) {
    return;
  }

  try {
    await stream.play();
    isAutoplayBlocked.value = false;
  } catch {
    isPaused.value = true;
    isAutoplayBlocked.value = true;
  }
}

function pauseCurrentVideo(): void {
  videoElement.value?.pause();
}

async function togglePlayback(): Promise<void> {
  if (isPaused.value) {
    await playCurrentVideo();

    return;
  }

  isPausedByViewer.value = true;
  pauseCurrentVideo();
}

function toggleMuted(): void {
  isMuted.value = !isMuted.value;
}

function resetSegmentState(): void {
  currentTime.value = 0;
  duration.value = 0;
  isPaused.value = true;
  isAutoplayBlocked.value = false;
  isCompleted.value = false;
}

async function selectSegment(index: number): Promise<void> {
  if (index < 0 || index >= props.videos.length || index === segmentIndex.value) {
    return;
  }

  pauseCurrentVideo();
  segmentIndex.value = index;
  resetSegmentState();
  emit('segment-change', index);
  await nextTick();
  await playCurrentVideo();
}

async function onLoadedMetadata(event: Event): Promise<void> {
  const element = event.target as HTMLVideoElement;
  duration.value = Number.isFinite(element.duration)
    ? element.duration
    : currentVideo.value?.duration ?? 0;

  await playCurrentVideo();
}

function onTimeUpdate(event: Event): void {
  currentTime.value = (event.target as HTMLVideoElement).currentTime;
}

function onPlay(): void {
  isPaused.value = false;
  isPausedByViewer.value = false;
}

function onPause(): void {
  isPaused.value = true;
}

async function onEnded(): Promise<void> {
  if (segmentIndex.value < props.videos.length - 1) {
    await selectSegment(segmentIndex.value + 1);

    return;
  }

  isCompleted.value = true;
  emit('finished');
  resetSegmentState();
  isCompleted.value = true;
  await nextTick();
  if (videoElement.value !== null) {
    videoElement.value.pause();
    videoElement.value.currentTime = 0;
  }
}

async function onError(): Promise<void> {
  if (stream.recover()) {
    return;
  }

  if (segmentIndex.value < props.videos.length - 1) {
    await selectSegment(segmentIndex.value + 1);

    return;
  }

  isCompleted.value = true;
  emit('finished');
}

watch(
  () => props.active,
  async (active, wasActive) => {
    if (!active) {
      pauseCurrentVideo();

      return;
    }

    if (wasActive) {
      return;
    }

    isPausedByViewer.value = false;
    if (isCompleted.value) {
      segmentIndex.value = 0;
      resetSegmentState();
    }

    await nextTick();
    await playCurrentVideo();
  },
);

watch(
  () => props.suspended,
  async (suspended) => {
    if (suspended) {
      pauseCurrentVideo();

      return;
    }

    if (!isPausedByViewer.value && !isCompleted.value) {
      await playCurrentVideo();
    }
  },
);

watch(
  () => props.videos,
  async () => {
    pauseCurrentVideo();
    segmentIndex.value = 0;
    resetSegmentState();
    await nextTick();
    await playCurrentVideo();
  },
);

onBeforeUnmount(() => {
  pauseCurrentVideo();
});

/** The desktop feed drives the active card from the keyboard (Space, M). */
defineExpose({ togglePlayback, toggleMuted });
</script>

<template>
  <div class="story-player absolute inset-0 h-full w-full overflow-hidden bg-black">
    <video
      v-if="shouldMountVideo && currentVideo !== null"
      ref="videoElement"
      :key="currentVideo.id"
      class="story-player__video"
      :poster="currentVideo.poster ?? undefined"
      :preload="preloadMode"
      :muted="isMuted"
      :autoplay="active && !suspended"
      playsinline
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
      @error="onError"
    />
    <div
      v-else
      class="story-player__video bg-black"
      aria-hidden="true"
    />

    <button
      class="story-player__surface"
      type="button"
      aria-label="Пауза или воспроизведение"
      @click="togglePlayback"
    />

    <Spinner
      v-if="active && isBuffering && !isAutoplayBlocked"
      class="pointer-events-none absolute left-1/2 top-1/2 z-20 size-12 -translate-x-1/2 -translate-y-1/2 text-white/80"
    />
    <button
      v-else-if="isAutoplayBlocked || (isPaused && !isBuffering)"
      class="story-player__play"
      type="button"
      :aria-label="isAutoplayBlocked ? 'Запустить видео' : 'Продолжить видео'"
      @click.stop="togglePlayback"
    >
      <Icon
        name="tabler:player-play-filled"
        class="size-8"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="videos.length > 0"
      class="story-player__progress"
      :class="sheet && '!bottom-4'"
      aria-label="Прогресс видео"
    >
      <button
        v-for="(video, index) in videos"
        :key="video.id"
        class="story-player__segment"
        type="button"
        :aria-label="`Перейти к видео ${index + 1}`"
        @click.stop="selectSegment(index)"
      >
        <span class="story-player__track" />
        <span
          class="story-player__fill"
          :style="{ width: `${getSegmentProgress(index)}%` }"
        />
      </button>
    </div>

    <slot
      :segment-index="segmentIndex"
      :segment="currentVideo"
      :progress="progress"
      :current-time="currentTime"
      :duration="duration"
      :remaining-label="remainingLabel"
      :is-paused="isPaused"
      :is-muted="isMuted"
      :is-autoplay-blocked="isAutoplayBlocked"
      :is-completed="isCompleted"
      :toggle-playback="togglePlayback"
      :toggle-muted="toggleMuted"
      :select-segment="selectSegment"
    />
  </div>
</template>

<style scoped>
.story-player__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.story-player__surface {
  position: absolute;
  inset: 0;
  z-index: 10;
  width: 100%;
  height: 100%;
  padding: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.story-player__play {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 20;
  display: grid;
  width: 4rem;
  height: 4rem;
  place-items: center;
  color: #fff;
  background: rgb(0 0 0 / 40%);
  border: 1px solid rgb(255 255 255 / 25%);
  border-radius: 999px;
  transform: translate(-50%, -50%);
  backdrop-filter: blur(4px);
}

.story-player__progress {
  position: absolute;
  right: 0;
  bottom: 5rem;
  left: 0;
  z-index: 35;
  display: flex;
  gap: 0.25rem;
  width: 100%;
  padding: 0 1.5rem;
}

.story-player__segment {
  position: relative;
  flex: 1 1 0;
  height: 0.5rem;
  min-width: 0;
  padding: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.story-player__track,
.story-player__fill {
  position: absolute;
  top: 50%;
  left: 0;
  height: 4px;
  border-radius: 999px;
  transform: translateY(-50%);
  transition: width 100ms linear;
}

.story-player__track {
  width: 100%;
  background: rgb(255 255 255 / 40%);
}

.story-player__fill {
  background: #fff;
}

@media (max-width: 767px) {
  .story-player__progress {
    bottom: 6.25rem;
    padding: 0 1.5rem;
  }
}
</style>
